// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as storageActions from 'admin/storage/actions';
import { combineReducers } from 'redux';
import {
    ActionType,
    getType,
} from 'typesafe-actions';
import {
    IPaginateApiResponse,
    paginateInitialState,
} from 'common/api/resources/Response';
import {
    IShortStorageResponse,
    IStorageDiskResponse,
    IStorageResponse,
} from 'common/api/resources/Storage';
import { StorageType } from 'common/api/resources/StorageType';
import {
    addToPaginated,
    appendToPaginated,
    deleteFromPaginated,
    updateInPaginated,
} from 'common/reducers/list';
import { COMPUTE_RESOURCE_STORAGE_STATUS } from 'admin/computeResource/constants';

interface IStorage {
    list: IPaginateApiResponse<IStorageResponse[]>;
    item: IStorageResponse;
    disks: IPaginateApiResponse<IStorageDiskResponse[]>;
}

export type StorageAction = ActionType<typeof storageActions>;
export type StorageState = IStorage;

export const initialShortStorageState: IShortStorageResponse = {
    id: 0,
    is_available_for_balancing: true,
    name: '',
    mount: '',
    path: '',
    thin_pool: '',
    free_space: 0,
    type: {
        name: StorageType.SHARED_LVM,
        formats: [],
    },
    credentials: {},
    status: COMPUTE_RESOURCE_STORAGE_STATUS.UNKNOWN,
    status_details: null,
};

export const initialStorageState: IStorageResponse = {
    id: 0,
    servers_count: 0,
    compute_resources_count: 0,
    is_available_for_balancing: true,
    isDeleting: false,
    isLoading: false,
    name: '',
    mount: '',
    path: '',
    thin_pool: '',
    free_space: 0,
    type: {
        name: StorageType.SHARED_LVM,
        formats: [],
    },
    credentials: {},
    status: undefined,
    status_details: undefined,
};

export default combineReducers<StorageState, StorageAction>({
    list: (state = paginateInitialState, action: StorageAction) => {
        switch (action.type) {
        case getType(storageActions.appendStorages):
            return appendToPaginated(state, action.payload);
        case getType(storageActions.addStorageItem):
            return addToPaginated(state, action.payload);
        case getType(storageActions.setStorageList):
            return action.payload;
        case getType(storageActions.removeStorageItem):
            return deleteFromPaginated(state, action.payload);
        case getType(storageActions.updateStorageItem):
            return updateInPaginated(state, action.payload);
        case getType(storageActions.setStorageItemIsDeleting):
            return updateInPaginated(state, action.payload);
        case getType(storageActions.setStorageItemIsLoading):
            return updateInPaginated(state, action.payload);
        default:
            return state;
        }
    },
    item: (state = initialStorageState, action) => {
        switch (action.type) {
        case getType(storageActions.setStorageItem):
            return action.payload;
        case getType(storageActions.unsetStorageItem):
            return { ...initialStorageState };
        default:
            return state;
        }
    },
    disks: (state = paginateInitialState, action) => {
        switch (action.type) {
        case getType(storageActions.setStorageDiskList):
            return action.payload;
        default:
            return state;
        }
    },

});
